<template>
    <x-container fluid app-width>
        <x-layout>
            <x-flex>
                <template v-if="loading">
                    <loading-spinner />
                </template>

                <x-container v-else fluid class="summary-container">
                    <x-layout>
                        <x-flex xs12>
                            <summary-tabs-buttons
                                v-model="activeTab"
                                :tabs="summaryTabs"
                            />
                        </x-flex>
                    </x-layout>

                    <x-layout v-if="risk.creation_method === 'automated'" mb-4>
                        <x-flex xs12>
                            <risk-banner>
                                <x-layout class="banner-header">
                                    <x-flex class="text-xs-left">
                                        <x-icon color="teal" class="mr-2">report</x-icon>
                                        Data was imported from BNPP
                                        &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;
                                        Ref: {{ risk.automated_submission_resource_id }}
                                        <span v-if="hasUnmatchedObligors">
                                        &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;
                                        {{ unmatchedObligorCount }} unmatched obligor(s)
                                    </span>
                                        <span v-if="hasUnmatchedGuarantors">
                                        &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;
                                        {{ unmatchedGuarantorCount }} unmatched guarantor(s)
                                    </span>
                                    </x-flex>

                                    <x-flex class="text-xs-right">
                                        <status-chip v-if="risk.firm_order_last_requested_at" small status="firm_order_requested" class="mr-2" />
                                        <button style="display: inline-flex; align-items: start;" @click="isExpanded = !isExpanded">
                                            <span style="text-decoration: underline">Send feedback to Insured</span>
                                            <i class="material-icons ml-2">{{ isExpanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}</i>
                                        </button>
                                    </x-flex>
                                </x-layout>
                                <x-layout v-if="isExpanded">
                                    <x-flex class="text-xs-left" style="margin-left: 28px">
                                        You must ensure the data that you are sending back to the client is valid and accurate. If you submit this feedback, you are confirming all feedback information is correct and up to date.
                                    </x-flex>
                                </x-layout>

                                <x-layout v-if="isExpanded && shouldShowAdditionalInformationMessage">
                                    <x-flex class="text-xs-left" style="margin-left: 28px">
                                        Additional information available for Insured.
                                    </x-flex>
                                </x-layout>

                                <x-layout v-if="isExpanded">
                                    <x-flex class="text-xs-left" style="margin-left: 28px">
                                        <enquiry-history-modal :manual-submission-id="risk.id" />
                                    </x-flex>
                                </x-layout>

                                <x-layout v-if="isExpanded">
                                    <x-flex xs12>
                                        <x-form ref="insuredResponsesForm" @submit.prevent="sendInsuredResponses">
                                            <x-data-table
                                                :headers="insurerFeedbackHeaders"
                                                :items="insuredResponses"
                                                class="mt-3 no-row-hover"
                                                hide-actions
                                            >
                                                <template v-slot:no-data>
                                                    <p class="error-message text-xs-center">No insurer feedback recorded</p>
                                                </template>

                                                <template v-slot:items="props">
                                                    <tr>
                                                        <td>{{ props.item.insurer_company_name }}</td>
                                                        <td>
                                                            <div>
                                                                {{ props.item.queued_at ? $moment(props.item.queued_at).format("DD MMM YYYY kk:mm") : '-' }}
                                                                <action-icon
                                                                    v-if="props.item.queued_at"
                                                                    :color="insuredFeedbackIconColor(props.item)"
                                                                >
                                                                    <template v-slot:icon="{ on }">
                                                                        <x-icon small :color="insuredFeedbackIconColor(props.item)" v-on="on">{{ insuredFeedbackIcon(props.item) }}</x-icon>
                                                                    </template>

                                                                    <p>{{ insuredFeedbackTooltipMessage(props.item) }}</p>
                                                                </action-icon>
                                                            </div>
                                                            <div v-if="props.item.queued_at" class="mt-1">
                                                                <x-dialog v-model="payloadModal[props.item.insurer_company_id]" persistent width="900" max-width="900">
                                                                    <template v-slot:activator="{ on }">
                                                                        <x-icon v-if="props.item.outdated_at" small color="yellow">warning</x-icon>
                                                                        <button v-on="on" type="button">
                                                                            <span style="text-decoration: underline">View payload</span>
                                                                        </button>
                                                                    </template>

                                                                    <x-container>
                                                                        <x-layout>
                                                                            <x-flex xs12>
                                                                                <h2>Response: {{ responseOptions.find(({ id }) => id === props.item.response_type).name }}</h2>
                                                                            </x-flex>
                                                                        </x-layout>

                                                                        <x-layout mt-3 v-if="props.item.outdated_at">
                                                                            <x-flex class="grow-0 pr-0">
                                                                                <x-icon color="yellow">warning</x-icon>
                                                                            </x-flex>
                                                                            <x-flex>
                                                                                <span class="outdated-warning">{{ $moment(props.item.outdated_at).format("DD MMM YYYY kk:mm") }}: <strong>Outdated Response</strong></span>
                                                                                <p>The client updated the enquiry after this feedback was sent.</p>
                                                                            </x-flex>
                                                                        </x-layout>

                                                                        <x-layout mt-3>
                                                                            <x-flex xs12>
                                                                                <data-list :value="props.item.payload"/>
                                                                            </x-flex>
                                                                        </x-layout>

                                                                        <x-layout class="response-actions">
                                                                            <x-flex xs12 class="text-xs-right">
                                                                                <x-btn
                                                                                    class="ma-2"
                                                                                    color="tertiary"
                                                                                    small
                                                                                    @click.stop="$set(payloadModal, props.item.insurer_company_id, false)"
                                                                                >
                                                                                    Close
                                                                                </x-btn>
                                                                            </x-flex>
                                                                        </x-layout>
                                                                    </x-container>
                                                                </x-dialog>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <x-select
                                                                :items="responseOptions"
                                                                :ref="`responseTypeField-${props.item.insurer_company_id}`"
                                                                :disabled="hasSatisfiedRequestedCapacity || !props.item.is_selected"
                                                                v-model="props.item.response_type"
                                                                placeholder=" "
                                                                append-icon="keyboard_arrow_down"
                                                                item-text="name"
                                                                item-value="id"
                                                                :menu-props="{
                                                                    offsetY: true,
                                                                    maxHeight: 'none',
                                                                    value: false,
                                                                    openOnClick: true,
                                                                    closeOnClick: true,
                                                                    closeOnContentClick: true,
                                                                    transition: 'scroll-y-transition'
                                                                }"
                                                                :class="{
                                                                    'input-no-margin': 'no-margin',
                                                                }"
                                                                class="mt-3"
                                                                :rules="[v => ruleRequiredIf(v, props.item.is_selected, true, 'Response type')]"
                                                            />
                                                        </td>
                                                        <td class="included-column text-xs-center">
                                                            <x-checkbox :disabled="hasSatisfiedRequestedCapacity" v-model="props.item.is_selected" @change="(newValue) => handleInsuredFeedbackToggle(props.item.insurer_company_id, newValue)" hide-details/>
                                                        </td>
                                                        <td class="text-xs-center">
                                                            <x-icon class="error-icon" v-if="props.item.errors[props.item.response_type]?.length">warning</x-icon>
                                                        </td>
                                                        <td>
                                                            <ul class="mt-1 error-text" v-for="error in (props.item.errors[props.item.response_type] ?? [])">
                                                                <li>{{error}}</li>
                                                            </ul>
                                                        </td>
                                                    </tr>
                                                </template>
                                            </x-data-table>

                                            <x-layout>
                                                <x-flex class="mt-3 text-xs-right">
                                                    <p v-if="hasSatisfiedRequestedCapacity" class="mt-2">
                                                        <action-icon
                                                            :color="capacitySatisfiedIconColor"
                                                        >
                                                            <template v-slot:icon="{ on }">
                                                                <x-icon small :color="capacitySatisfiedIconColor" v-on="on">{{ capacitySatisfiedIcon }}</x-icon>
                                                            </template>

                                                            <p>{{ capacitySatisfiedTooltipMessage }}</p>
                                                        </action-icon>

                                                        Capacity satisfied: {{ $moment(this.risk.satisfaction_of_requested_capacity_queued_at).format("DD MMM YYYY kk:mm") }}
                                                    </p>
                                                    <div v-else>
                                                        <x-btn @click="markCapacitySatisfied" color="tertiary" class="mr-3">
                                                            Mark Capacity Satisfied
                                                        </x-btn>
                                                        <x-btn :disabled="!canSendInsuredResponses" type="submit" color="primary">
                                                            Submit
                                                            <i class="material-icons ml-2">trending_flat</i>
                                                        </x-btn>
                                                    </div>
                                                    <p v-if="risk.firm_order_last_requested_at" class="mt-2">
                                                        Firm order requested: {{ $moment(risk.firm_order_last_requested_at).format("DD MMM YYYY kk:mm") }}
                                                    </p>
                                                </x-flex>
                                            </x-layout>
                                        </x-form>
                                    </x-flex>
                                </x-layout>
                            </risk-banner>
                        </x-flex>

                        <modal
                            ref="insuredFeedbackQueuedModal"
                            title="Feedback queued"
                        >
                            <p>The insurer feedback has been queued and will be sent soon.</p>
                            <p>The status will update on the MST landing page within 30 seconds.</p>
                            <p>You can close this window and check back for updates.</p>
                        </modal>
                    </x-layout>

                    <template v-if="activeTab === 'product'">
                        <x-layout>
                            <x-flex xs6 class="left">
                                <x-card class="panel mb-5">
                                    <x-card-title>
                                        <h3>Submission information</h3>
                                    </x-card-title>

                                    <x-card-text>
                                        <data-list v-model="summaryData" />
                                    </x-card-text>
                                </x-card>

                                <x-card class="panel" v-if="Boolean(risk.additional_information)">
                                    <x-card-title>
                                        <h3>Additional information</h3>
                                    </x-card-title>

                                    <x-card-text>
                                        <p class="additional-information">{{ risk.additional_information }}</p>
                                    </x-card-text>
                                </x-card>
                            </x-flex>

                            <x-flex xs6 class="right">
                                <x-card v-if="this.risk.entities.length" class="panel mb-5">
                                    <x-card-title>
                                        <h3>Entities</h3>
                                    </x-card-title>

                                    <x-card-text>
                                        <m-s-t-name-address-table
                                            :items="risk.entities"
                                            :headers="entityTableHeaders"
                                        />
                                    </x-card-text>
                                </x-card>

                                <x-card class="panel mb-5">
                                    <x-card-title>
                                        <h3>Obligors</h3>
                                        <action-icon
                                            v-if="hasUnmatchedObligors || exceedsObligorCap"
                                            color="teal"
                                            icon="report"
                                        >
                                            <template v-slot:icon="{ on }">
                                                <x-icon color="teal" class="ml-2" v-on="on">
                                                    report
                                                </x-icon>
                                            </template>

                                            <p v-if="hasUnmatchedObligors">
                                                The Obligor received via API has no exact matches in the Tepfin X database. Please add the Obligor manually and then click 'Edit' and enter the Obligor name in the enquiry form.
                                            </p>
                                            <p v-if="exceedsObligorCap">
                                                The client sent too many obligors. Only the main obligor has been imported.
                                            </p>
                                        </action-icon>
                                    </x-card-title>

                                    <x-card-text>
                                        <m-s-t-name-address-table
                                            :items="formattedObligors"
                                            :headers="obligorTableHeaders"
                                        />
                                    </x-card-text>
                                </x-card>

                                <x-card class="panel">
                                    <x-card-title>
                                        <h3>Guarantors</h3>
                                        <action-icon
                                            v-if="hasUnmatchedGuarantors || exceedsGuarantorCap"
                                            color="teal"
                                            icon="report"
                                        >
                                            <template v-slot:icon="{ on }">
                                                <x-icon color="teal" class="ml-2" v-on="on">
                                                    report
                                                </x-icon>
                                            </template>

                                            <p v-if="hasUnmatchedGuarantors">
                                                The Guarantor received via API has no exact matches in the Tepfin X database. Please add the Guarantor manually and then click 'Edit' and enter the Guarantor name in the enquiry form.
                                            </p>
                                            <p v-if="exceedsGuarantorCap">
                                                The client sent too many guarantors. No guarantors have been imported.
                                            </p>
                                        </action-icon>
                                    </x-card-title>

                                    <x-card-text>
                                        <m-s-t-name-address-table
                                            :items="formattedGuarantors"
                                            :headers="guarantorTableHeaders"
                                        />
                                    </x-card-text>
                                </x-card>
                            </x-flex>
                        </x-layout>
                    </template>

                    <template v-if="activeTab === 'market_response_sheet'">
                        <x-layout>
                            <x-flex xs12 class="right">
                                <x-card class="panel">
                                    <x-card-title>
                                        <h3>Market response sheet</h3>
                                    </x-card-title>

                                    <x-card-text>
                                        <x-data-table
                                            class="no-row-hover"
                                            :headers="mrsTableHeaders"
                                            :items="risk.insurers"
                                            hide-actions
                                        >
                                            <template v-slot:no-data>
                                                <p class="error-message text-xs-center">No data found</p>
                                            </template>
                                            <template v-slot:items="props">
                                                <tr>
                                                    <td>
                                                        {{ (props.item.insurer_company) ? props.item.insurer_company.name : 'N/A' }}
                                                        <br>
                                                        <br>
                                                        Security: {{ (props.item.security) ? props.item.security.name : 'N/A' }}
                                                    </td>
                                                    <td>
                                                        {{ (props.item.underwriter) ? props.item.underwriter.name : 'N/A'}}
                                                    </td>
                                                    <td>
                                                        {{ (props.item.indicated_line_size_currency) ? props.item.indicated_line_size_currency.name : 'N/A'}}
                                                    </td>
                                                    <td>
                                                        {{ props.item.indicated_line_size | currency }}
                                                    </td>
                                                    <td>
                                                        {{ props.item.pricingTypePretty }}
                                                    </td>
                                                    <td>
                                                        {{ props.item.statusPretty }}
                                                    </td>
                                                    <td>
                                                        {{ props.item.feedback }}
                                                    </td>
                                                    <td>
                                                        {{ props.item.subjectivities }}
                                                    </td>
                                                    <td>
                                                        {{ props.item.brokerage | percent}}
                                                    </td>
                                                </tr>
                                            </template>
                                        </x-data-table>
                                    </x-card-text>
                                </x-card>
                            </x-flex>
                        </x-layout>
                    </template>

                    <template v-if="activeTab === 'documents'">
                        <document-list
                            :insurer-list="insurerList"
                            insurer-specific-upload
                            upload
                            :facility-id="risk.facility_id"
                            :documentable-id="risk.id"
                            documentable-type="manual_submission"
                        />
                    </template>
                </x-container>
            </x-flex>
        </x-layout>
    </x-container>
</template>

<script>
import SummaryTabsButtons from '~/components/Shared/UIComponents/SummaryTabsButtons';
import StandalonePolicySummaryDataList
    from "../../../../components/Shared/UIComponents/StandalonePolicySummaryDataList.vue";
import DataList from "../../../../components/Shared/UIComponents/DataList.vue";
import DocumentList from "../../../../components/Shared/UIComponents/DocumentList.vue";
import MSTNameAddressTable from "../../../../components/Broker/ManualSubmissionTracker/MSTNameAddressTable.vue";
import _ from "lodash";
import Big from "big.js";
import RiskBanner from "../../../../components/Shared/UIComponents/RiskBanner.vue";
import rules from '~/mixins/rules';
import ActionIcon from '~/components/Shared/UIComponents/ActionIcon';
import Modal from '~/components/Shared/UIComponents/Modal';
import StatusChip from '~/components/Shared/UIComponents/StatusChip';
import EnquiryHistoryModal from '~/components/Broker/ManualSubmissionTracker/EnquiryHistoryModal';

export default {
    name: 'ManualSubmissionSummary',
    props: {
        risk: {
            type: Object,
            required: true
        }
    },
    components: {
        RiskBanner,
        SummaryTabsButtons,
        MSTNameAddressTable,
        DocumentList,
        DataList,
        StandalonePolicySummaryDataList,
        ActionIcon,
        Modal,
        StatusChip,
        EnquiryHistoryModal,
    },
    mixins: [rules],
    data() {
        return {
            loading: false,
            isExpanded: false,
            activeTab: 'product',
            insuredResponses: [],
            responseOptions: [
                {
                    id: 'no_thank_you',
                    name: 'No Thank You (NTY)',
                },
                {
                    id: 'expression_of_interest',
                    name: 'Expression of Interest (EOI)',
                },
                {
                    id: 'non_binding_indication',
                    name: 'Non-Binding Indication (NBI)'
                }
            ],
            summaryTabs: [
                {
                    value: 'product',
                    text: 'Product',
                },
                {
                    value: 'market_response_sheet',
                    text: 'Market Response Sheet',
                },
                {
                    value: 'documents',
                    text: 'Documents',
                },
            ],
            openSubmissionInformationPanel: 0,
            actionMenuItems: [
                {
                    name: 'Convert to Manual Firm Order',
                    eventName: 'action-convert-to-manual-firm-order'
                },
                {
                    name: 'Edit',
                    eventName: 'action-edit-manual-submission'
                }
            ],
            insurerFeedbackHeaders: [
                {
                    text: 'Insurer',
                    sortable: false,
                    width: '16%',
                },
                {
                    text: 'Last Update Sent',
                    sortable: false,
                    width: '13%'
                },
                {
                    text: 'Response Type',
                    sortable: false,
                    width: '26%',
                },
                {
                    text: 'Select',
                    sortable: false,
                    width: '7%',
                    align: 'center'
                },
                {
                    text: 'Error(s)',
                    sortable: false,
                    width: '10%',
                    align: 'center'
                },
                {
                    text: 'Detail Of Error(s)',
                    sortable: false,
                    width: '28%',
                },
            ],
            entityTableHeaders: [
                {
                    text: 'Entity',
                    value: 'value.name'
                },
                {
                    text: 'Entity address',
                    value: 'value.address.fullAddress'
                },
            ],
            obligorTableHeaders: [
                {
                    text: 'Obligor',
                    value: 'value.name'
                },
                {
                    text: 'Obligor address',
                    value: 'value.address.fullAddress'
                },
            ],
            guarantorTableHeaders: [
                {
                    text: 'Guarantor',
                    value: 'value.name'
                },
                {
                    text: 'Guarantor address',
                    value: 'value.address.fullAddress'
                },
            ],
            signedLinesTableHeaders: [
                {
                    text: 'Insurer / Security',
                    value: 'value.name'
                },
                {
                    text: 'Brokerage',
                    value: 'value.name'
                },
                {
                    text: 'Written line',
                    value: 'value.name'
                },
                {
                    text: 'Premium rate',
                    value: 'value.name'
                },
            ],
            mrsTableHeaders: [
                {
                    text: 'Insurer / Security',
                    value: 'value.name'
                },
                {
                    text: 'Underwriter',
                    value: 'value.name'
                },
                {
                    text: 'Indicated line size currency',
                    value: 'value.name'
                },
                {
                    text: 'Indicated line size',
                    value: 'value.name'
                },
                {
                    text: 'Pricing type',
                    value: 'value.name'
                },
                {
                    text: 'Status',
                    value: 'value.name'
                },
                {
                    text: 'Feedback',
                    value: 'value.name'
                },
                {
                    text: 'Subjectivities',
                    value: 'value.name'
                },
                {
                    text: 'Brokerage',
                    value: 'value.name'
                },
            ],
            payloadModal: {},
        };
    },
    async mounted() {
        this.$eventBus.$emit('update-action-menu-items', this.actionMenuItems);
        this.$eventBus.$on('action-edit-manual-submission', this.editMST);
        this.$eventBus.$on('action-convert-to-manual-firm-order', this.convertToManualFirmOrder);

        const response = (await axios.get(this.$root.api(`broker/manual-submissions/${this.risk.id}/insured-feedback/errors`)));
        this.insuredResponses = this.risk.insurers.map(({
            insurer_company: {name, id},
            latest_insured_feedback,
            outdated_at,
        }) => ({
            insurer_company_name: name,
            insurer_company_id: id,
            queued_at: latest_insured_feedback?.queued_at,
            succeeded_at: latest_insured_feedback?.succeeded_at,
            failed_at: latest_insured_feedback?.failed_at,
            response_type: latest_insured_feedback?.response_type,
            payload: this.formatPayload(latest_insured_feedback?.payload ?? {}),
            is_selected: true,
            errors: response.data.find(({insurer_company_id}) => insurer_company_id === id) ?? {},
            outdated_at,
        }));
    },
    beforeDestroy() {
        this.$eventBus.$off('action-edit-manual-submission', this.editMST);
        this.$eventBus.$off('action-convert-to-manual-firm-order', this.convertToManualFirmOrder);
    },
    computed: {
        capacitySatisfiedIconColor() {
            const {
                satisfaction_of_requested_capacity_succeeded_at: succeeded_at,
                satisfaction_of_requested_capacity_failed_at: failed_at,
            } = this.risk;

            if (failed_at) {
                return 'poppy';
            }

            if (succeeded_at) {
                return 'secondary';
            }

            return 'mustard';
        },
        capacitySatisfiedIcon() {
            const {
                satisfaction_of_requested_capacity_succeeded_at: succeeded_at,
                satisfaction_of_requested_capacity_failed_at: failed_at,
            } = this.risk;

            if (failed_at) {
                return 'error';
            }

            if (succeeded_at) {
                return 'check';
            }

            return 'timer';
        },
        capacitySatisfiedTooltipMessage() {
            const {
                satisfaction_of_requested_capacity_succeeded_at: succeeded_at,
                satisfaction_of_requested_capacity_failed_at: failed_at,
            } = this.risk;

            if (failed_at) {
                return "Failed to mark capacity as satisfied (" + this.$moment(failed_at).format("DD MMM YYYY kk:mm") + ")";
            }

            if (succeeded_at) {
                return "Capacity successfully marked as satisfied (" + this.$moment(succeeded_at).format("DD MMM YYYY kk:mm") + ")";
            }

            return "Marking capacity as satisfied has been queued";
        },
        shouldShowAdditionalInformationMessage() {
            const hasNeverSentResponses = this.insuredResponses.some(({queued_at}) => queued_at === null);
            const hasResponsesSentInDifferentBatches = [...new Set(this.insuredResponses.map(({queued_at}) => queued_at))].length > 1;

            return hasNeverSentResponses || hasResponsesSentInDifferentBatches;
        },
        unmatchedObligorCount() {
            return this.risk.obligors.filter(({obligor_id}) => obligor_id === null).length;
        },
        unmatchedGuarantorCount() {
            return this.risk.guarantors.filter(({guarantor_id}) => guarantor_id === null).length;
        },
        hasUnmatchedObligors() {
            return this.unmatchedObligorCount > 0;
        },
        hasUnmatchedGuarantors() {
            return this.unmatchedGuarantorCount > 0;
        },
        exceedsObligorCap() {
            return this.risk.exceeds_obligor_cap;
        },
        exceedsGuarantorCap() {
            return this.risk.exceeds_guarantor_cap;
        },
        formattedObligors() {
            return this.risk.obligors.map(({ obligor, source_name, source_country }) => ({
                name: obligor?.name ?? source_name,
                address: {
                    fullAddress: obligor?.address?.fullAddress ?? source_country,
                }
            }))
        },
        formattedGuarantors() {
            return this.risk.guarantors.map(({ guarantor, source_name, source_country }) => ({
                name: guarantor?.name ?? source_name,
                address: {
                    fullAddress: guarantor?.address?.fullAddress ?? source_country,
                }
            }))
        },
        summaryData() {
            let data = [
                {
                    text: 'Insured',
                    value: this.risk.client_company.name
                },
                {
                    text: 'Insured address',
                    value: this.risk.client_company.address ? this.risk.client_company.address.fullAddress : '/'
                },
                {
                    text: 'Facility name',
                    value: this.risk.facility?.name
                },
                {
                    text: 'Facility UMR',
                    value: this.risk.facility?.UMR
                }
            ];

            if (this.risk.entities.length === 1) {
                data.push({
                        text: 'Entity',
                        value: this.risk.entities[0].name
                    },
                    {
                        text: 'Entity Address',
                        value: this.risk.entities[0].address ? this.risk.entities[0].address.fullAddress : '/'
                    });
            }

            data.push(
                 {
                    text: 'Client reference',
                    value: this.risk.client_reference
                },
                {
                    text: 'Category',
                    value: this.risk.category?.name
                },
                {
                    text: 'Product',
                    value: this.risk.product?.name
                }
            )

            if (this.risk.loan_radar_id) {
                data.push({
                    text: 'Loan radar ID (if applicable)',
                    value: this.risk.loan_radar_id
                })
            }

            data.push(
                {
                    text: 'Policy currency',
                    value: this.risk.currency.name
                },
                {
                    text: 'Total limit of liability',
                    value: this.$options.filters.currency(this.risk.limit_of_liability, this.risk.currency.code)
                },
                {
                    text: 'Total facility amount',
                    value: this.$options.filters.currency(this.risk.total_facility_amount, this.risk.currency.code)
                },
                {
                    text: 'Total Risk Period - Years & days',
                    value: this.$options.filters.yearsAndDays(this.risk.total_risk_period)
                }
            )

            if (this.risk.utilisation_purpose) {
                data.push({
                    text: 'Utilisation purpose',
                    value: this.risk.utilisation_purpose
                })
            }

            if (this.risk.repayment_structure) {
                data.push({
                    text: 'Repayment structure',
                    value: this.risk.repayment_structure
                })
            }

            if (this.risk.business_line) {
                data.push({
                    text: 'Business line',
                    value: this.risk.business_line
                })
            }

            if (this.risk.gross_margin) {
                data.push({
                    text: 'Gross margin',
                    value: this.$options.filters.currency(this.risk.gross_margin, 'BPS')
                })
            }

            if (this.risk.net_margin) {
                data.push({
                    text: 'Net margin',
                    value: this.$options.filters.currency(this.risk.net_margin, 'BPS')
                })
            }

            data.push(
                {

                    text: 'Estimated revenue (this financial year)',
                    value: this.$options.filters.currency(this.risk.estimated_revenue_this_year, this.risk.currency.code)
                },
                {
                    text: 'Estimated revenue (policy life)',
                    value: this.$options.filters.currency(this.risk.estimated_revenue_policy_life, this.risk.currency.code)
                },
                {
                    text: 'Secured?',
                    value: this.$options.filters.yesOrNo(this.risk.is_secured)
                },
                {
                    text: 'Date enquiry received',
                    value: this.$options.filters.formatDate(this.risk.date_enquiry_received, null, 'YYYY-MM-DD')
                },
                {
                    text: 'Anticipated bind date',
                    value: this.$options.filters.formatDate(this.risk.anticipated_bind_date, null, 'YYYY-MM-DD')
                },
                {
                    text: 'Start date',
                    value: this.$options.filters.formatDate(this.risk.inception_date, null, 'YYYY-MM-DD')
                },
                {
                    text: 'End date',
                    value: this.$options.filters.formatDate(this.risk.expiry_date, null, 'YYYY-MM-DD')
                },
                {
                    text: 'Probability of binding',
                    value: this.$options.filters.percent(this.risk.probability_of_binding)
                },
                {
                    text: 'Placing broker contact',
                    value: this.risk.placing_broker_contact?.name
                },
            );

            return data;
        },
        insurerList() {
            return this.risk.insurers.map(ins => ins.insurer_company);
        },
        hasSatisfiedRequestedCapacity() {
            return Boolean(this.risk.satisfaction_of_requested_capacity_queued_at);
        },
        canSendInsuredResponses() {
            if (this.hasSatisfiedRequestedCapacity) {
                return false;
            }

            return this.insuredResponses.some(({is_selected}) => is_selected) &&
                !this.insuredResponses.some(({response_type, errors}) => {
                    return errors[response_type]?.length;
                });
        }
    },
    methods: {
        formatPayload(payload) {
            return Object.keys(payload).map((key) => ({
                text: key,
                value: payload[key],
            }));
        },
        handleInsuredFeedbackToggle(id, newValue) {
            if (!newValue) {
                this.insuredResponses.find(({insurer_company_id}) => insurer_company_id === id).response_type = null;
                this.$refs[`responseTypeField-${id}`].validate();
            }
        },
        async markCapacitySatisfied() {
            await axios.put(this.$root.api(`broker/manual-submissions/${this.risk.id}/insured-feedback/satisfaction-of-requested-capacity`));
            this.$router.go();
        },
        async sendInsuredResponses() {
            if (this.$refs.insuredResponsesForm.validate()) {
                await axios.post(this.$root.api(`broker/manual-submissions/${this.risk.id}/insured-feedback`), {
                    feedback: this.insuredResponses
                        .filter(({is_selected}) => is_selected)
                        .map(({insurer_company_id, response_type}) => ({
                            insurer_company_id,
                            response_type,
                        }))
                });
                await this.$refs.insuredFeedbackQueuedModal.open();
                this.$router.go();
            }
        },
        editMST() {
            this.$router.push({path: `/agent/mst/${this.risk.id}`});
        },
        convertToManualFirmOrder() {
            this.$router.push(
                {
                    name: 'broker-new-risk',
                    params: {
                        submissionCopy: this.prepareSubmissionForConversionToManualFirmOrder(),
                        isConvertingFromManualSubmission: true,
                        id: 'new'
                    }
                }
            );
        },
        prepareSubmissionForConversionToManualFirmOrder() {
            let submission = _.cloneDeep(this.risk);

            submission.is_manual_firm_order = 1;
            submission.risk_type = 'submission';
            submission.contract_value = submission.total_facility_amount;
            if (submission.limit_of_liability && submission.total_facility_amount) {
                submission.indemnity = Number((Big(Big(submission.limit_of_liability).div(submission.total_facility_amount)).times(100)).toFixed(12));
            }
            submission.country_id = submission.obligors.length > 0 ? submission.obligors[0].country_id : null;

            submission.product_data = {};
            if (submission.category?.name === 'Loans') {
                submission.product_data.loan_radar_id = submission.loan_radar_id;
            }
            if (submission.category?.name === 'BNPP / GreenStars Insurance / Reinsurance - All Risks') {
                submission.product_data.repayment_structure = submission.repayment_structure;
                submission.product_data.business_line = submission.business_line;
            }

            submission.obligors = submission.obligors.map(({ obligor_id, country_id, id}) => ({
                id: obligor_id,
                country_id,
                pivot: {
                    id,
                    obligor_id,
                    sub_limit: null,
                }
            }));
            submission.guarantors = submission.guarantors.map(({ guarantor_id, country_id, id}) => ({
                id: guarantor_id,
                country_id,
                pivot: {
                    id,
                    guarantor_id,
                }
            }));
            submission.entities = submission.entities.map(ent => {
                ent.entity_id = ent.id
                return ent;
            });
            submission.information_sections = [];

            if (submission.product?.key === 'equity') {
                submission.obligors = [];
                submission.guarantors = [];
            }

            return submission;
        },
        insuredFeedbackIconColor({succeeded_at, failed_at}) {
            if (failed_at) {
                return 'poppy';
            }

            if (succeeded_at) {
                return 'secondary';
            }

            return 'mustard';
        },
        insuredFeedbackIcon({succeeded_at, failed_at}) {
            if (failed_at) {
                return 'error';
            }

            if (succeeded_at) {
                return 'check';
            }

            return 'timer';
        },
        insuredFeedbackTooltipMessage({succeeded_at, failed_at}) {
            if (failed_at) {
                return "Failed to send feedback to Insured (" + this.$moment(failed_at).format("DD MMM YYYY kk:mm") + ")";
            }

            if (succeeded_at) {
                return "Feedback successfully sent to Insured (" + this.$moment(succeeded_at).format("DD MMM YYYY kk:mm") + ")";
            }

            return "Feedback queued for sending";
        }
    }
}
</script>

<style scoped lang="scss">
.additional-information {
    background: rgba(255, 255, 255, 0.05);
    padding: 4px 12px;
    white-space: pre-line;
    font-size: rem(14px);
    line-height: rem(20px);
}

.banner-header {
    color: $color-teal;
    align-items: baseline;
}

.included-column {
    .x-input {
        justify-content: center;
    }
}

.error-icon {
    color: $color-mustard;
}


.error-text {
    color: mix(#FFFFFF, $color-mustard, 80%);
    list-style-type: disc;
    padding-left: 20px;
}

.x-datatable td {
    padding: 0;
}

.outdated-warning {
    color: $color-yellow;
}
</style>
